/**
 * <nia-cart-drawer>
 * Carrito lateral. Las cantidades se cambian con la Cart Ajax API y el markup
 * se vuelve a pedir con la Section Rendering API, así el precio, la barra de
 * envío y el subtotal se recalculan en Liquid y no se duplica el formato de
 * moneda en JavaScript.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
const SECTION_ID = 'nia-cart-drawer';

class NiaCartDrawer extends HTMLElement {
  #lastFocus = null;

  connectedCallback() {
    this.panel = this.querySelector('.nia-cart__panel');

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    // Cualquier alta al carrito del tema (ficha de producto, quick add) abre el drawer.
    document.addEventListener('shopify:cart:lines-update', this.#onExternalUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:cart:lines-update', this.#onExternalUpdate);
    document.body.style.overflow = '';
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open = () => {
    if (this.isOpen) return;
    this.#lastFocus = document.activeElement;
    this.classList.add('is-open');
    this.panel?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';

    // preventScroll no es un lujo: en este instante el panel todavía está en
    // translateX(100%), o sea fuera de cuadro a la derecha. Sin la bandera, el
    // navegador desplaza la ventana para revelar lo que acaba de enfocar, y
    // como enseguida se bloquea el scroll del body, el drawer se queda
    // colgando a medio salir.
    this.panel?.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
  };

  close = () => {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.panel?.setAttribute('inert', '');
    document.body.style.overflow = '';
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  };

  toggle = () => {
    this.isOpen ? this.close() : this.open();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-nia-cart-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const control = target.closest('[data-nia-cart-line]');
    if (control instanceof HTMLElement) {
      event.preventDefault();
      this.#change(Number(control.dataset.niaCartLine), Number(control.dataset.niaCartQty));
    }
  };

  #onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.panel) return;

    const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** Una alta hecha fuera del drawer refresca el contenido y lo abre. */
  #onExternalUpdate = (event) => {
    if (this.dataset.updating === 'true') return;
    event.promise?.then(() => this.#refresh().then(this.open)).catch(() => {});
  };

  /**
   * @param {number} line Índice de línea, base 1.
   * @param {number} quantity Cantidad nueva. 0 elimina la línea.
   */
  async #change(line, quantity) {
    if (!Number.isFinite(line) || !Number.isFinite(quantity) || quantity < 0) return;

    this.dataset.updating = 'true';
    this.setAttribute('busy', '');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: SECTION_ID }),
      });

      if (!response.ok) throw new Error(`cart/change.js respondió ${response.status}`);

      const data = await response.json();
      this.#render(data.sections?.[SECTION_ID]);
      this.#announce(data.item_count);
    } catch (error) {
      console.warn('[nia-cart-drawer] No se pudo actualizar el carrito:', error);
    } finally {
      this.removeAttribute('busy');
      this.dataset.updating = 'false';
    }
  }

  /** Refresca el contenido y abre. Lo usa el quick add de las tarjetas. */
  openWithRefresh = async () => {
    await this.#refresh();
    this.open();
  };

  /** Vuelve a pedir el markup sin cambiar cantidades. */
  async #refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`);
      if (!response.ok) return;
      const data = await response.json();
      this.#render(data[SECTION_ID]);
    } catch (error) {
      console.warn('[nia-cart-drawer] No se pudo refrescar:', error);
    }
  }

  /**
   * Sustituye solo la región marcada, no el elemento completo: así el drawer
   * conserva su estado de abierto y el foco no salta al reemplazar el DOM.
   * @param {string | undefined} html
   */
  #render(html) {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-refresh]');
    const current = this.querySelector('[data-cart-refresh]');
    if (!fresh || !current) return;

    current.innerHTML = fresh.innerHTML;

    // El conteo viene en el markup recién renderizado, así que la burbuja del
    // header se sincroniza sin pedir /cart.js por separado.
    const count = Number(fresh.dataset.count);
    if (Number.isFinite(count)) {
      current.dataset.count = String(count);
      this.#announce(count);
    }
  }

  /** Mantiene sincronizada la burbuja del header sin recargar la página. */
  #announce(itemCount) {
    if (typeof itemCount !== 'number') return;
    document.dispatchEvent(
      new CustomEvent('nia:cart:updated', { detail: { itemCount }, bubbles: true })
    );
  }
}

if (!customElements.get('nia-cart-drawer')) {
  customElements.define('nia-cart-drawer', NiaCartDrawer);
}
